import type { EnvironmentId } from "@cmmd-forge/contracts";

import { deriveLogicalProjectKey } from "./logicalProject";
import type { Project } from "./types";

/**
 * An Environment as far as Project placement is concerned.
 *
 * Deliberately a narrow structural shape rather than a saved-registry record or
 * a cloud catalog row: placement is a decision about which durable execution
 * target hosts a Project, and the only facts that decision needs are the
 * Environment's identity, something to show a human, and whether it hosts
 * Projects at all.
 *
 * Note what is NOT here, by construction: no sidebar grouping mode, no sort
 * order, no display filter. Placement decides where code physically lives, so a
 * display preference must never be able to reach it — the way to guarantee that
 * is for the preference to be unspellable in this module's inputs rather than
 * merely unread by its bodies.
 */
export interface PlacementEnvironment {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  /**
   * False for an Environment that exists but hosts no Projects — today, the
   * Credential Vault (see CONTEXT.md § Cloud execution), which is a per-user
   * Environment holding provider credentials and is never user-facing.
   */
  readonly hostsProjects: boolean;
}

/**
 * The Environments a Project can actually be placed on.
 *
 * The filter is the whole point: a Credential Vault is an Environment by every
 * other measure — it is a durable execution target with an identity — so
 * counting Environments without asking whether they host Projects would show a
 * placement control to every Seat that owns exactly one real Environment.
 */
export function resolvePlacementEligibleEnvironments(
  environments: readonly PlacementEnvironment[],
): readonly PlacementEnvironment[] {
  return environments.filter((environment) => environment.hostsProjects);
}

/**
 * Does this Seat see a placement control at all?
 *
 * False while a Seat owns one Environment: the first Environment silently
 * absorbs every new Project, so a Seat that owns one never encounters placement.
 * The surface appears only once a second Environment exists.
 */
export function isPlacementChoiceOffered(environments: readonly PlacementEnvironment[]): boolean {
  return resolvePlacementEligibleEnvironments(environments).length >= 2;
}

/**
 * Where a Project lives, and whether the customer had to say so.
 *
 * `absorbed` and `assigned` name the same outcome — a Project on an Environment
 * — reached two different ways, and the distinction is the feature: `absorbed`
 * is the answer a Seat with one Environment always gets and never sees, while
 * `assigned` is a choice that Seat made. Collapsing them would lose the only
 * signal a caller has for whether to render placement at all.
 */
export type ProjectPlacement =
  | { readonly kind: "absorbed"; readonly environmentId: EnvironmentId }
  | { readonly kind: "assigned"; readonly environmentId: EnvironmentId }
  | { readonly kind: "unplaced" }
  | { readonly kind: "no-environment" };

export interface ResolveProjectPlacementInput {
  /** From `deriveLogicalProjectKey` — see `placementKeyForProject`. */
  readonly logicalProjectKey: string;
  readonly environments: readonly PlacementEnvironment[];
  readonly assignments: ReadonlyMap<string, EnvironmentId>;
}

export function resolveProjectPlacement(input: ResolveProjectPlacementInput): ProjectPlacement {
  const eligible = resolvePlacementEligibleEnvironments(input.environments);
  if (eligible.length === 0) return { kind: "no-environment" };

  // Absorb BEFORE reading the assignment, not after. A Seat that owns one
  // Environment never encounters placement, and that has to hold on the way
  // down to one as well as on the way up from it: a downgrade archives an
  // Environment but leaves behind the assignments that named the survivor, and
  // reporting those as a choice would make placement re-appear for a Seat that
  // is once again supposed to never see it.
  const sole = eligible.length === 1 ? eligible[0] : undefined;
  if (sole) return { kind: "absorbed", environmentId: sole.environmentId };

  const assigned = input.assignments.get(input.logicalProjectKey);
  if (assigned !== undefined && eligible.some((e) => e.environmentId === assigned)) {
    return { kind: "assigned", environmentId: assigned };
  }

  return { kind: "unplaced" };
}

/**
 * The key a Project is placed by.
 *
 * A thin pass-through to `deriveLogicalProjectKey` on purpose. Placement needs
 * a stable identity for "this Project, wherever it is checked out", and that
 * derivation already is one — normalised remote suffixed by in-repo subpath, so
 * three worktrees of one package share it while two packages of one monorepo do
 * not. A second derivation here would be a second answer to the same question,
 * and the two would disagree the first time either changed.
 *
 * Specifically NOT `canonicalRepoKeyOf`: that reduces both sides of a
 * comparison to `host/owner/name` so a group key can be matched against
 * whatever repository URL CMMD stored. Dropping the subpath is right for
 * matching and wrong for identity.
 */
export function placementKeyForProject(
  project: Pick<Project, "environmentId" | "id" | "repositoryIdentity">,
): string {
  return deriveLogicalProjectKey(project);
}
